import React, { useState, useEffect, useRef } from 'react';
import { 
    FileText, 
    Upload, 
    Download, 
    Trash2, 
    Save, 
    X,
    File,
    Image
} from 'react-feather';
import api, { projectApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isManager } from '../../utils/roleUtils';
import { Button } from '../ui/button';
import './ProjectNotesArtifacts.css';

const ProjectNotesArtifacts = ({ projectId, project }) => {
    const { user } = useAuth();
    const [notes, setNotes] = useState('');
    const [originalNotes, setOriginalNotes] = useState('');
    const [artifacts, setArtifacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [userIsManager, setUserIsManager] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        checkUserRole();
        fetchData();
    }, [projectId]);

    const checkUserRole = async () => {
        try {
            const response = await api.get('/api/auth/role');
            const userRole = response.data?.role;
            setUserIsManager(isManager(userRole));
        } catch (err) {
            console.error('Error checking user role:', err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch project with notes and artifacts
            const projectData = await projectApi.get(projectId);
            setNotes(projectData.notes || '');
            setOriginalNotes(projectData.notes || '');
            setArtifacts(projectData.artifacts || []);
        } catch (err) {
            console.error('Error fetching project data:', err);
            setError('Failed to load notes and artifacts');
        } finally {
            setLoading(false);
        }
    };

    const handleNotesChange = (e) => {
        setNotes(e.target.value);
    };

    const handleSaveNotes = async () => {
        if (!userIsManager) {
            setError('Only managers can edit notes');
            return;
        }

        try {
            setSaving(true);
            setError('');
            await projectApi.updateNotes(projectId, notes);
            setOriginalNotes(notes);
        } catch (err) {
            console.error('Error saving notes:', err);
            setError('Failed to save notes');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelNotes = () => {
        setNotes(originalNotes);
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (!userIsManager) {
            setError('Only managers can upload artifacts');
            return;
        }

        try {
            setUploading(true);
            setError('');
            const uploadedArtifacts = await projectApi.uploadArtifacts(projectId, files);
            setArtifacts(prev => [...uploadedArtifacts, ...prev]);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (err) {
            console.error('Error uploading artifacts:', err);
            setError('Failed to upload files');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteArtifact = async (artifactId) => {
        if (!userIsManager) {
            setError('Only managers can delete artifacts');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this artifact?')) {
            return;
        }

        try {
            setError('');
            await projectApi.deleteArtifact(projectId, artifactId);
            setArtifacts(prev => prev.filter(a => a.id !== artifactId));
        } catch (err) {
            console.error('Error deleting artifact:', err);
            setError('Failed to delete artifact');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) {
            return <Image size={18} />;
        } else if (fileType === 'application/pdf') {
            return <FileText size={18} />;
        } else {
            return <File size={18} />;
        }
    };

    const hasNotesChanges = notes !== originalNotes;

    if (loading) {
        return <div className="project-notes-artifacts-loading">Loading...</div>;
    }

    return (
        <div className="project-notes-artifacts">
            {error && (
                <div className="project-notes-artifacts-error">
                    {error}
                </div>
            )}

            {/* Notes Section */}
            <div className="project-notes-section">
                <div className="project-notes-header">
                    <h3 className="project-notes-title">
                        <FileText size={18} />
                        Project Notes
                    </h3>
                    {userIsManager && hasNotesChanges && (
                        <div className="project-notes-actions">
                            <Button
                                variant="neutral-tertiary"
                                onClick={handleCancelNotes}
                                disabled={saving}
                            >
                                <X size={16} />
                                Cancel
                            </Button>
                            <Button
                                variant="brand-primary"
                                onClick={handleSaveNotes}
                                disabled={saving}
                            >
                                <Save size={16} />
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    )}
                </div>
                <textarea
                    className="project-notes-editor"
                    value={notes}
                    onChange={handleNotesChange}
                    placeholder="Add project notes, documentation, or any relevant information here..."
                    disabled={!userIsManager}
                    rows={10}
                />
                {!userIsManager && (
                    <div className="project-notes-readonly-message">
                        Only managers can edit project notes
                    </div>
                )}
            </div>

            {/* Artifacts Section */}
            <div className="project-artifacts-section">
                <div className="project-artifacts-header">
                    <h3 className="project-artifacts-title">
                        <File size={18} />
                        Project Artifacts
                    </h3>
                    {userIsManager && (
                        <div className="project-artifacts-upload">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            <Button
                                variant="brand-primary"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                            >
                                <Upload size={16} />
                                {uploading ? 'Uploading...' : 'Upload Files'}
                            </Button>
                        </div>
                    )}
                </div>

                {artifacts.length === 0 ? (
                    <div className="project-artifacts-empty">
                        <File size={48} />
                        <p>No artifacts uploaded yet</p>
                        {userIsManager && (
                            <p className="project-artifacts-empty-hint">
                                Click "Upload Files" to add documentation, password documents, release documentation, etc.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="project-artifacts-list">
                        {artifacts.map((artifact) => (
                            <div key={artifact.id} className="project-artifact-item">
                                <div className="project-artifact-icon">
                                    {getFileIcon(artifact.file_type)}
                                </div>
                                <div className="project-artifact-info">
                                    <div className="project-artifact-name">{artifact.file_name}</div>
                                    <div className="project-artifact-meta">
                                        <span>{formatFileSize(artifact.file_size)}</span>
                                        <span>•</span>
                                        <span>
                                            {new Date(artifact.created_at).toLocaleDateString()}
                                        </span>
                                        {artifact.uploaded_by_user && (
                                            <>
                                                <span>•</span>
                                                <span>
                                                    {artifact.uploaded_by_user.display_name || artifact.uploaded_by_user.email}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="project-artifact-actions">
                                    <Button
                                        variant="neutral-tertiary"
                                        onClick={() => window.open(artifact.url, '_blank')}
                                        title="Download"
                                    >
                                        <Download size={16} />
                                    </Button>
                                    {userIsManager && (
                                        <Button
                                            variant="neutral-tertiary"
                                            onClick={() => handleDeleteArtifact(artifact.id)}
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectNotesArtifacts;

